// Vue
import { defineComponent, ref, PropType, onMounted, watch, computed } from "vue";

// Providers
import { Phase } from "@/providers/Construction/InspectConstructionProvider";
import { KeyedStringEnum } from "@/providers/Materials/LibraryEnumsProvider";

// Services
import { getIndicatorTooltip } from "@/services/Views/MaterialDatabase/IndicatorTooltip";

// Types
import { Material } from "@/types/Views/MaterialDatabase/Material";

// Components
import { VerticalBarChart, ChartTooltipEventType } from "@/features/Charts";
import SearchSelectWithKeyValue from "@/components/Reusables/Inputs/SearchSelectWithKeyValue.vue";
import { useTooltip } from "@stores/useTooltip";

// Utils
import format from "@/services/Utils/format";

const CompareGraph = defineComponent({
    props: {
        material: {
            type: Object as PropType<Material>,
            required: true,
        },
        includedPhases: {
            type: Array as PropType<number[]>,
        },
        indicatorTypeEnums: {
            type: Object as PropType<KeyedStringEnum>,
            required: true,
        },
        indicatorEnums: {
            type: Object as PropType<KeyedStringEnum>,
            required: true,
        },
    },
    emits: ["mappingNameClicked"],
    setup(props) {
        const chartData = ref();
        const tooltip = useTooltip();

        function getUnitFromIndicatorType(indicatorType: number) {
            return props.indicatorEnums[props.indicatorTypeEnums[indicatorType]] || " - ";
        }

        const indicatorOptions = computed(() => {
            const indicators = new Set<number>();
            for (const lciaResult of props.material.lciaResults) {
                for (const indicator of lciaResult.indicators) {
                    indicators.add(indicator.type);
                }
            }

            return Array.from(indicators).map((indicator) => ({
                name: `${props.indicatorTypeEnums[indicator]} - ${getIndicatorTooltip(indicator)} (${getUnitFromIndicatorType(
                    indicator
                )})`,
                value: indicator,
            }));
        });

        function getIndicatorOptions() {
            return indicatorOptions.value;
        }

        // Indicator type ids, same values project.filters.ts uses for the default library filter.
        const GWP_INDICATOR_TYPE = 0;
        const GWP_IOBC_INDICATOR_TYPE = 56;

        function getDefaultIndicator() {
            const options = indicatorOptions.value;
            return (
                options.find((option) => option.value === GWP_INDICATOR_TYPE)?.value ??
                options.find((option) => option.value === GWP_IOBC_INDICATOR_TYPE)?.value ??
                options[0]?.value ??
                -1
            );
        }

        const selectedIndicator = ref(getDefaultIndicator());

        function getChartData() {
            const entries = [];

            for (const lciaResult of props.material.lciaResults) {
                if ([1, 2, 3].includes(lciaResult.moduleTypeEnum)) continue;

                if (props.includedPhases && !props.includedPhases.includes(lciaResult.moduleTypeEnum)) {
                    continue;
                }

                const indicator = lciaResult.indicators.find((i) => i.type === selectedIndicator.value);

                if (indicator && indicator.value !== null) {
                    entries.push({
                        label: Phase[lciaResult.moduleTypeEnum],
                        value: indicator.value,
                    });
                }
            }

            chartData.value = {
                indicator: {
                    name: "indicator",
                    entries,
                },
            };
        }

        const formatTooltipText = ({ event, unit }: { event: ChartTooltipEventType; unit: string }) => {
            return `
        <div class="flex flex-col text-white">
        <div class="mb-px-6 h-px-8 w-px-24 rounded-4" style="background-color: ${event?.color || "transparent"}"></div>
          <span class="t-caption-bold">${event?.name ?? ""}</span>
          <span class="t-caption">${format.decimalNumber(event?.value as number)} ${unit}</span>
        </div>
      `;
        };

        const onHover = (event: ChartTooltipEventType) => {
            tooltip.showTooltip(formatTooltipText({ event, unit: currentUnit.value }));
        };

        const onLeave = () => {
            tooltip.hideTooltip();
        };

        const currentUnit = computed(() => {
            return getUnitFromIndicatorType(selectedIndicator.value);
        });

        const searchText = ref(indicatorOptions.value.find((o) => o.value === selectedIndicator.value)?.name ?? "");

        watch(
            () => selectedIndicator.value,
            (val) => {
                searchText.value = indicatorOptions.value.find((o) => o.value === val)?.name ?? "";
            }
        );

        onMounted(() => {
            getChartData();
        });

        watch(
            () => props.material,
            () => getChartData(),
            { deep: true }
        );

        watch(
            () => selectedIndicator.value,
            () => getChartData()
        );

        function setSelectedIndicator(indicator: string) {
            selectedIndicator.value = Number(indicator);
        }

        return {
            chartData,
            selectedIndicator,
            searchText,
            setSelectedIndicator,
            currentUnit,
            getIndicatorOptions,
            onHover,
            onLeave,
        };
    },
    render() {
        const { chartData, selectedIndicator, searchText, setSelectedIndicator, currentUnit, getIndicatorOptions, onHover, onLeave } = this;

        return selectedIndicator > -1 ? (
            <div class="flex h-fit w-fit flex-col items-center justify-end ">
                <div class="z-10 -mb-px-24 flex w-full items-center justify-between space-x-px-16 px-px-24 pl-px-48 text-type-default">
                    <SearchSelectWithKeyValue
                        label="Indicator"
                        options={getIndicatorOptions()}
                        placeholder="Select indicator"
                        modelValue={searchText}
                        // Without a defaultValue the child blanks the label when the async enums land and its options change.
                        defaultValue={selectedIndicator}
                        hideClear={true}
                        onUpdate:modelValue={(val: string | null) => (this.searchText = val ?? "")}
                        onOptionSelected={setSelectedIndicator}
                    />
                </div>
                <div class="h-[360px] w-[450px]">
                    <VerticalBarChart
                        data={chartData}
                        unit={currentUnit}
                        onHover={onHover}
                        onLeave={onLeave}
                    />
                </div>
            </div>
        ) : null;
    },
});

export default CompareGraph;
